import { db } from "@utils/IndexedDb";
import { contentfulImageLoader } from "@utils/imageLoader";
import { useLiveQuery } from "dexie-react-hooks";
import { motion } from "framer-motion";
import { FC, useEffect, useRef, useState } from "react";

import useMediaQuery from "@/hooks/useMediaQuery";
import { MediaBannerHighlightContent, MediaBannerHighlightType } from "@/types";
import cn from "@/utils/cn";

import MediaBannerHighlightCardContent from "./MediaBannerHighlightCardContent";

export interface Props {
  content: MediaBannerHighlightContent;
  animation?: boolean;
  type: MediaBannerHighlightType | string;
  boxAlignment: "left" | "center" | "right" | string;
  wrappedText?: boolean;
}

export interface ProjectId {
  projectInfo?: { id: string };
}

const MediaBannerHighlightCard: FC<Props> = ({ content, type, animation, boxAlignment, wrappedText, ...rest }) => {
  const slug = content?.primaryCta?.embeddedVideo?.link?.content?.slug;
  const container = useRef<HTMLDivElement>(null);
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const gradientBg = content?.gradientColor && content.gradientColor?.value?.replace(/rgb/i, "rgba").replace(/\)/i, ",0.4)");
  const mobileWrappedText = !isDesktop && wrappedText;
  const [isPinned, setIsPinned] = useState(false);

  const rawBgImageUrl = (isDesktop ? content.image?.url : content.mobileImage?.url || content.image?.url) || "";
  const isBoxed = type !== MediaBannerHighlightType.FULL_SCREEN;
  const bgWidth = isDesktop ? (isBoxed ? 1680 : 1920) : isBoxed ? 390 : 750;
  const bgImageUrl = rawBgImageUrl ? contentfulImageLoader({ src: rawBgImageUrl, width: bgWidth, quality: 80 }) : undefined;

  const projectId: ProjectId = { ...rest };
  const propertyTypeRecords = useLiveQuery(async () => {
    const projectCount = await db.PROJECTS.where("id")
      .equals(projectId.projectInfo?.id || "")
      .first();
    return projectCount || null;
  });
  // console.log("propertyTypeRecords", propertyTypeRecords);
  const projectData = useLiveQuery(async () => {
    const projectCount = await db.PROJECTS.where("slug")
      .equals(slug || "")
      .first();
    return projectCount || null;
  });
  // console.log("propertyTypeRecords", projectData);
  const contentData = { ...content, projectData: projectData };

  const getSiblings = (elm: any) => elm && elm.parentNode && [...elm.parentNode.children].filter((node) => node != elm);

  useEffect(() => {
    if (container.current && type === MediaBannerHighlightType.FULL_SCREEN && animation) {
      const siblings = getSiblings(container.current);

      if (!isPinned) return;

      siblings.forEach((sibling: HTMLElement) => {
        if (!sibling.classList.contains("fixed") && !sibling.classList.contains("sticky")) {
          sibling.style.setProperty("z-index", "1");
          sibling.style.setProperty("position", "relative");

          // avoid attaching arbitrary classes that causes visibility issues
          // if (!sibling.className.includes("bg-") && !sibling.style.backgroundImage && !sibling.style.backgroundColor) {
          //   sibling.classList.add("bg-white");
          // }
        }
      });
    }
  }, [container, type, animation, isPinned]);

  if (type === MediaBannerHighlightType.FULL_SCREEN) {
    return (
      <motion.div className="relative h-lvh" ref={container} onViewportEnter={() => setIsPinned(true)} onViewportLeave={() => setIsPinned(false)}>
        <div className={cn("w-dvw md:w-full h-lvh", animation && isPinned && "fixed top-0 left-0 ")}>
          {content.video ? (
            <video className={cn(`h-full w-full object-cover`)} poster={content.backupVideoImage?.url} muted autoPlay loop playsInline>
              <source src={isDesktop ? content.video.url : content.mobileVideo?.url} />
            </video>
          ) : (
            <div className={cn(`h-full w-full bg-cover`)} style={{ backgroundImage: `url(${bgImageUrl})` }}></div>
          )}
          <div
            className={cn(
              "md:w-8\\/24 absolute left-0 md:left-auto md:top-[50%] md:translate-y-[-50%]",
              !wrappedText
                ? "top-0 h-full md:h-auto w-full"
                : "w-22\\/24 top-[50%] translate-y-[-50%] left-[50%] translate-x-[-50%] md:translate-x-0",
              boxAlignment === "left" && "md:left-[4.2%] ",
              boxAlignment === "right" && "md:right-[4.2%]",
              boxAlignment === "center" && "md:left-[50%] md:translate-x-[-50%]",
            )}
          >
            <MediaBannerHighlightCardContent content={content} type={type} wrappedText={mobileWrappedText} {...rest} />
            {/* {propertyTypeRecords?.inventoryAvailable && (
              <MediaBannerHighlightCardContent content={content} type={type} wrappedText={wrappedText} {...rest} />
            )} */}
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <div className={cn("relative mx-auto w-22\\/24 x-ray", type === MediaBannerHighlightType.BOXED_CAROUSEL ? "my-m md:my-3xl" : "my-m md:my-xxl")}>
      <style jsx>{`
        .x-ray::after {
          ${content.gradientColor && `background-color:${gradientBg}`};
        }
      `}</style>
      <div className={cn(" relative ")}>
        <div
          className={cn(
            "aspect-[0.55] md:aspect-[2.5] flex md:px-[4.2%]",
            type !== MediaBannerHighlightType.BOXED_CAROUSEL && (mobileWrappedText || isDesktop)
              ? "items-center mx-m md:mx-0"
              : "items-end pb-m md:pb-0",
            boxAlignment === "left" && "justify-start",
            boxAlignment === "right" && "justify-end items-center",
            boxAlignment === "center" && "justify-center",
          )}
        >
          {content.video ? (
            <video
              className={`w-full object-cover absolute top-0 left-0 h-full rounded-2xl overflow-hidden`}
              poster={content.backupVideoImage?.url}
              muted
              autoPlay
              loop
              playsInline
            >
              <source src={isDesktop ? content.video.url : content.mobileVideo?.url} />
            </video>
          ) : (
            <div
              className={cn(`h-full w-full bg-cover absolute top-0 left-0 bg-center rounded-2xl overflow-hidden`)}
              style={{ backgroundImage: `url(${bgImageUrl})` }}
            ></div>
          )}
          <div className={cn("w-full md:w-8\\/24")}>
            <MediaBannerHighlightCardContent content={contentData} type={type} wrappedText={mobileWrappedText} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaBannerHighlightCard;
